import React, { useState } from 'react';
import NewMoneySalePriceButtonProperties from "./NewMoneySalePriceButtonProperties";
import {useI18nContext} from "../../../contexts/I18nContext";
import RoundedButton from "../../../ui_components/RoundedButton/RoundedButton";
import Modal from "../../../ui_components/Modal/Modal";
import {Observer} from "mobx-react-lite";
import {useStyles} from "./NewMoneySalePriceButton.style";
import Button from "../../../ui_components/Button/Button";
import ButtonType from "../../../ui_components/Button/ButtonType";
import {useCommonContext} from "../../../contexts/CommonContext";
import ButtonSize from "../../../ui_components/Button/ButtonSize";
import TextInput from '../../../ui_components/TextInput/TextInput';
import TextInputType from '../../../ui_components/TextInput/TextInputType';
import Icon from '../../../ui_components/Icon/Icon';
import IconName from '../../../ui_components/Icon/enums/IconName';
import Access from "../../Access/AllowAccess/Access";
import {useUserContext} from "../../../contexts/UserContext";
import PriceInput from '../../Inputs/PriceInput/PricetInput';
import Checkbox from '../../../ui_components/Checkbox/Checkbox';
import {Percent} from "@mui/icons-material";
import MoneyDeliveryFeeCurrencyValue from '../../../entities/productOrder/moneyDelivery/MoneyDeliveryFeeCurrencyValue';
import NewMoneySalePriceStore from './NewMoneySalePriceButtonStore';
import Currency from '../../../entities/Currency';

const NewMoneySalePriceButton: React.FC<NewMoneySalePriceButtonProperties> = ({
                                                                  onClick = () => {
                                                                  },
                                                                  onSave = () => {
                                                                  },
                                                                  showIcon = true,
                                                                  showText = true,
                                                                  disabled,
                                                                  className = '',
                                                                  salePriceId,
                                                                  moneySalePrice,
                                                                  rounded = false,
                                                                  size = ButtonSize.DEFAULT,
                                                                  type = ButtonType.DEFAULT
                                                              }) => {
    const classes = useStyles(showText);

    const i18n = useI18nContext();
    const commonContext = useCommonContext();
    const store = new NewMoneySalePriceStore(commonContext, salePriceId, moneySalePrice);

    const userStore = useUserContext();
    if (!userStore.hasAccess(Access.CREATE_SALE_PRICE.valueOf())) {
        return <></>;
    }

    const handleOnclick = async () => {
        await store.openModal();
        onClick();
    }

    const onCloseModal = () => {
        store.closeModal();
    }

    const CurrencyValueEdit: React.FC<{ 
        currencyValue: MoneyDeliveryFeeCurrencyValue, 
        onChange: (updatedValue: MoneyDeliveryFeeCurrencyValue) => void 
    }> = ({ currencyValue, onChange }) => {
        const [isPercent, setIsPercent] = useState<boolean>(currencyValue.isPercentage);
    
        const onValueChange = (newValue: number) => {
            onChange(new MoneyDeliveryFeeCurrencyValue(currencyValue.currency, newValue, isPercent));
        };
    
        const onIsPercentChange = (newValue: boolean) => {
            setIsPercent(newValue);
            onChange(new MoneyDeliveryFeeCurrencyValue(currencyValue.currency, currencyValue.value, newValue));
        };
    
        return (
            <div className={classes.payment_fee_row_container}>
                <div className={classes.payment_type}>
                    {i18n.translate(currencyValue.currency)}
                </div>
                <PriceInput 
                    onChange={onValueChange} 
                    value={currencyValue.value} 
                    icon={isPercent ? Percent : undefined}
                    label={isPercent ? "Porciento" : "Valor fijo"} 
                />
                <Checkbox text={"Valor en %"} onChange={onIsPercentChange} selected={isPercent} />
            </div>
        );
    };

    const onFromValueChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const newToValue =  event.target.value;
            store.moneySalePrice.fromValue = +newToValue;
    }

    const onToValueChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const newToValue =  event.target.value;
            store.moneySalePrice.toValue = +newToValue;
    }

    const updateCurrencyValue = (currency: Currency, updatedValue: MoneyDeliveryFeeCurrencyValue) => {
        const index = store.moneyDeliveryFeeCurrencyValues.findIndex(item => item.currency === currency);
        if (index !== -1) {
            store.moneyDeliveryFeeCurrencyValues[index] = updatedValue;
        }
    };

    const onSalePriceValuesSave = async () => {
        const saved = await store.save();
        if (saved) {
            store.closeModal();
            if (onSave) {
                onSave();
            }
        }
    }

    const currencies = [Currency.DOLLAR, Currency.MLC, Currency.MN];

    const text = moneySalePrice ? "Editar precio de venta" : "Nuevo precio de venta";
    const btnIcon = moneySalePrice ? <Icon name={IconName.EDIT_ICON}/> : <Icon name={IconName.CREATE_ICON}/>;
    const Btn = () => {
        if (rounded) {
            return <RoundedButton
                onClick={handleOnclick}
                className={className}
                disabled={disabled}
                tooltip={!showText ? text : ""}
                text={showText ? text : ""}
                type={type}
                icon={showIcon ? btnIcon : <></>}
            />
        }
        return <Button
            text={showText ? text : ""}
            className={className}
            tooltip={!showText ? text : ""}
            disabled={disabled}
            size={size}
            onClick={handleOnclick}
            type={type}
            startIcon={showIcon ? btnIcon : <></>}
        />
    }
    return <Observer>
        {() => (
            <>
                <Modal fullScreen={false} loading={store.loading} title={text}
                    onClose={onCloseModal}
                    onPressEnter={onSalePriceValuesSave}
                    open={store.isModalOpen}>
                    <div className={classes.modal_container}>
                        <div className={classes.from_to_inputs_container}>
                            <TextInput
                                type={TextInputType.NUMBER}
                                value={moneySalePrice?.fromValue}
                                onChange={onFromValueChange}
                                label={i18n.translate("FROM")}
                                className={classes.input}
                                fullWidth
                                autoFocus
                            />
                            <TextInput
                                type={TextInputType.NUMBER}
                                value={moneySalePrice?.toValue}
                                onChange={onToValueChange}
                                label={i18n.translate("TO")}
                                className={classes.input}
                                fullWidth
                            />
                        </div>

                        <div className={classes.currency_value_container}>
                            {currencies.map((currency: Currency, i) => {
                                const tempObject = store.moneyDeliveryFeeCurrencyValues.find(b => b.currency === currency)!!;
                                return <CurrencyValueEdit
                                    currencyValue={tempObject}
                                    key={i}
                                    onChange={(updatedValue) => updateCurrencyValue(currency, updatedValue)}
                                />
                            })}
                        </div>

                        <Button
                            loading={store.loading} className={classes.input} onClick={onSalePriceValuesSave}
                            type={ButtonType.SECONDARY}
                            text={i18n.translate("SAVE")}/>
                    </div>
                </Modal>
                <Btn/>
            </>
        )}
    </Observer>
}

export default NewMoneySalePriceButton;
